import React, { useState, useRef, useEffect } from 'react';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase.config';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Avatar,
  Paper,
  IconButton,
  Slide,
  Badge,
  Button,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon
} from '@mui/icons-material';

// Custom Chatbot SVG Icon for FAB
const CustomBotFabIcon = () => (
  <svg width="32" height="32" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g>
      <ellipse cx="256" cy="320" rx="192" ry="144" fill="#F4F8FB"/>
      <ellipse cx="256" cy="320" rx="144" ry="112" fill="#E3F2FD"/>
      <path d="M256 64c-106 0-192 86-192 192 0 106 86 192 192 192s192-86 192-192c0-106-86-192-192-192z" fill="#F4F8FB"/>
      <ellipse cx="256" cy="256" rx="144" ry="112" fill="#1976D2"/>
      <ellipse cx="256" cy="256" rx="112" ry="88" fill="#0D47A1"/>
      <ellipse cx="196" cy="256" rx="32" ry="40" fill="#40BFFF"/>
      <ellipse cx="316" cy="256" rx="32" ry="40" fill="#40BFFF"/>
      <rect x="232" y="32" width="48" height="48" rx="24" fill="#40BFFF"/>
      <rect x="240" y="16" width="32" height="32" rx="16" fill="#1976D2"/>
      <ellipse cx="64" cy="320" rx="32" ry="48" fill="#40BFFF"/>
      <ellipse cx="448" cy="320" rx="32" ry="48" fill="#40BFFF"/>
      <ellipse cx="416" cy="104" rx="16" ry="16" fill="#E3F2FD"/>
      <ellipse cx="96" cy="104" rx="16" ry="16" fill="#E3F2FD"/>
      <ellipse cx="416" cy="104" rx="8" ry="8" fill="#1976D2"/>
      <ellipse cx="96" cy="104" rx="8" ry="8" fill="#1976D2"/>
      <ellipse cx="416" cy="104" rx="4" ry="4" fill="#fff"/>
      <ellipse cx="96" cy="104" rx="4" ry="4" fill="#fff"/>
      <ellipse cx="432" cy="72" rx="8" ry="8" fill="#E3F2FD"/>
      <ellipse cx="80" cy="72" rx="8" ry="8" fill="#E3F2FD"/>
      <ellipse cx="432" cy="72" rx="4" ry="4" fill="#1976D2"/>
      <ellipse cx="80" cy="72" rx="4" ry="4" fill="#1976D2"/>
      <ellipse cx="432" cy="72" rx="2" ry="2" fill="#fff"/>
      <ellipse cx="80" cy="72" rx="2" ry="2" fill="#fff"/>
      <ellipse cx="400" cy="56" rx="8" ry="8" fill="#E3F2FD"/>
      <ellipse cx="112" cy="56" rx="8" ry="8" fill="#E3F2FD"/>
      <ellipse cx="400" cy="56" rx="4" ry="4" fill="#1976D2"/>
      <ellipse cx="112" cy="56" rx="4" ry="4" fill="#1976D2"/>
      <ellipse cx="400" cy="56" rx="2" ry="2" fill="#fff"/>
      <ellipse cx="112" cy="56" rx="2" ry="2" fill="#fff"/>
      <ellipse cx="384" cy="40" rx="8" ry="8" fill="#E3F2FD"/>
      <ellipse cx="128" cy="40" rx="8" ry="8" fill="#E3F2FD"/>
      <ellipse cx="384" cy="40" rx="4" ry="4" fill="#1976D2"/>
      <ellipse cx="128" cy="40" rx="4" ry="4" fill="#1976D2"/>
      <ellipse cx="384" cy="40" rx="2" ry="2" fill="#fff"/>
      <ellipse cx="128" cy="40" rx="2" ry="2" fill="#fff"/>
      {/* Chat bubble */}
      <ellipse cx="400" cy="80" rx="48" ry="32" fill="#E3F2FD"/>
      <ellipse cx="400" cy="80" rx="40" ry="24" fill="#B0BEC5"/>
      <ellipse cx="390" cy="80" rx="6" ry="6" fill="#1976D2"/>
      <ellipse cx="410" cy="80" rx="6" ry="6" fill="#1976D2"/>
      <ellipse cx="430" cy="80" rx="6" ry="6" fill="#1976D2"/>
    </g>
  </svg>
);

// ---------- Transition ----------
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ---------- Categories (OmneVu Invoicing/Quotation) ----------
const questionCategories = {
  general: {
    name: "General",
    questions: [
      "How do I create a new invoice?",
      "How do I generate a quotation?",
      "How do I save and download a PDF?"
    ]
  },
  invoice: {
    name: "Invoice",
    questions: [
      "How do I add products to an invoice?",
      "How do I edit invoice details?",
      "How do I send an invoice to a client?"
    ]
  },
  quotation: {
    name: "Quotation",
    questions: [
      "How do I customize a quotation template?",
      "How do I add terms and conditions?",
      "How do I track sent quotations?"
    ]
  },
  user: {
    name: "User Management",
    questions: [
      "How do I add a new user?",
      "How do I assign admin rights?",
      "How do I reset a user password?"
    ]
  },
  platform: {
    name: "About InvoQuot",
    questions: [
      "What is InvoQuot?",
      "How do I change the color scheme?",
      "Where can I find support?"
    ]
  }
};

// ---------- Knowledge Base (OmneVu Invoicing/Quotation) ----------
const knowledgeBase = {
  "how do i create a new invoice?": "Go to the Invoice section and click 'Add Invoice'. Fill in the client and product details, then save or download as PDF.",
  "how do i generate a quotation?": "Navigate to the Quotation section, click 'Add Quotation', enter the required details, and save or download the quotation as PDF.",
  "how do i save and download a pdf?": "After filling invoice or quotation details, click the 'Save & Download PDF' button at the bottom of the form.",

  "how do i add products to an invoice?": "While creating or editing an invoice, use the 'Add Product' button to include products/services. Enter quantity and price for each.",
  "how do i edit invoice details?": "Open the invoice you want to edit, make changes to client, product, or amount fields, and save your changes.",
  "how do i send an invoice to a client?": "After saving, you can download the PDF and email it to your client, or use the platform's share/send feature if available.",

  "how do i customize a quotation template?": "Go to Quotation Templates, select a template, and use the edit options to change layout, colors, and add your logo.",
  "how do i add terms and conditions?": "In the quotation or invoice form, look for the 'Terms and Conditions' section and enter your custom terms before saving.",
  "how do i track sent quotations?": "Check the Quotation List page to see all sent quotations and their status (sent, accepted, rejected, etc.).",

  "how do i add a new user?": "Go to User Management, click 'Add User', fill in the details, and assign a role (admin/user).",
  "how do i assign admin rights?": "In User Management, edit the user and select the 'Admin' role to grant admin privileges.",
  "how do i reset a user password?": "Select the user in User Management and use the 'Reset Password' option. The user will receive a reset link via email.",

  "what is invoquot?": "InvoQuot is a platform for creating, managing, and sharing invoices and quotations with customizable templates and user management.",
  "how do i change the color scheme?": "Go to Settings or Template Editor to select your preferred color scheme for invoices, quotations, and the app interface.",
  "where can i find support?": "Visit the Help section or contact support@omnevusolutions.com for assistance."
};

// ---------- Component ----------

const FloatingChatbot = () => { 
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm your InvoQuot Assistant. Ask me about invoices, quotations, templates, or user management!",
      sender: 'bot',
      timestamp: new Date(),
      isQuestion: false
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [activeCategory, setActiveCategory] = useState('general');
  const [showHint, setShowHint] = useState(true);
  const messagesEndRef = useRef(null);

  // Show hint for 4 seconds on mount
  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleOpen = () => { setOpen(true); setHasNewMessage(false); setShowHint(false); };
  const handleClose = () => setOpen(false);

  const formatTime = (date) => new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const pickKB = (text) => {
    const t = text.toLowerCase();
    if (knowledgeBase[t]) return knowledgeBase[t];
    if (t.includes('invoice')) return knowledgeBase["how do i create a new invoice?"];
    if (t.includes('quotation')) return knowledgeBase["how do i generate a quotation?"];
    if (t.includes('template')) return knowledgeBase["how do i customize a quotation template?"];
    if (t.includes('user')) return knowledgeBase["how do i add a new user?"];
    if (t.includes('color')) return knowledgeBase["how do i change the color scheme?"];
    if (t.includes('support') || t.includes('help')) return knowledgeBase["where can i find support?"];
    if (/\b(hi|hello|hey)\b/.test(t)) return "Hello! I can help with invoices, quotations, templates, and user management.";
    return null;
  };

  const botReply = async (text) => {
    const normalizedText = text.trim().toLowerCase();
    let found = null;

    try {
      // 1. Check answered questions
      const qSnap = await getDocs(collection(db, 'chatbot_questions'));
      for (const doc of qSnap.docs) {
        const data = doc.data();
        if (data.question && data.answer) {
          const dbQuestion = data.question.trim().toLowerCase();
          if (dbQuestion.includes(normalizedText) || normalizedText.includes(dbQuestion)) {
            found = data.answer.trim();
            break;
          }
        }
      }

      // 2. If not found, check unanswered (avoid duplicates)
      if (!found) {
        const uSnap = await getDocs(collection(db, 'unanswered_questions'));
        for (const doc of uSnap.docs) {
          const data = doc.data();
          if (data.question) {
            const dbQuestion = data.question.trim().toLowerCase();
            if (dbQuestion.includes(normalizedText) || normalizedText.includes(dbQuestion)) {
              found = "We already noted this question earlier. Our team will update with an answer soon.";
              break;
            }
          }
        }
      }
    } catch (e) {
      console.error("Error checking Firestore:", e);
    }

    // 3. If found → reply
    if (found) {
      setMessages((prev) => [...prev, { id: Date.now(), text: found, sender: 'bot', timestamp: new Date() }]);
      return;
    }

    // 4. Else → check knowledge base
    const reply = pickKB(text);
    if (reply) {
      setMessages((prev) => [...prev, { id: Date.now(), text: reply, sender: 'bot', timestamp: new Date() }]);
    } else {
      // 5. Save as new unanswered
      setMessages((prev) => [...prev, { id: Date.now(), text: "Thanks! We'll get back with an answer soon.", sender: 'bot', timestamp: new Date() }]);
      try {
        await addDoc(collection(db, 'unanswered_questions'), { question: normalizedText, timestamp: new Date().toISOString() });
      } catch (e) {
        console.error("Error saving unanswered question:", e);
      }
    }
  };

  const simulateBotResponse = (userMessage) => {
    setIsTyping(true);
    setTimeout(async () => {
      await botReply(userMessage);
      setIsTyping(false);
      if (!open) setHasNewMessage(true);
    }, 900);
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;
    const msg = inputMessage;
    setMessages((prev) => [...prev, { id: Date.now(), text: msg, sender: 'user', timestamp: new Date() }]);
    setInputMessage('');
    simulateBotResponse(msg);
  };

  const handleQuickQuestion = (q) => {
    setMessages((prev) => [...prev, { id: Date.now(), text: q, sender: 'user', timestamp: new Date(), isQuestion: true }]);
    simulateBotResponse(q);
    setActiveCategory(null);
  };

  const renderSuggestedQuestions = () => activeCategory && (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{questionCategories[activeCategory]?.name}:</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {questionCategories[activeCategory]?.questions.map((q, i) => (
          <Chip key={i} label={q} size="small" onClick={() => handleQuickQuestion(q)} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    </Box>
  );

  const renderCategoryButtons = () => (
    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {Object.keys(questionCategories).map((catKey) => (
        <Button key={catKey} variant="outlined" size="small"
          onClick={() => setActiveCategory(catKey)}
          sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.75rem', borderColor: '#1976D2', color: '#1976D2' }}>
          {questionCategories[catKey].name}
        </Button>
      ))}
    </Box>
  );

  return (
    <>
      {/* Floating Action Button */}
  <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 20000, pointerEvents: 'auto' }}>
    {/* Animated hint pop-up */}
    {showHint && (
      <Box
        sx={{
          position: 'absolute',
          bottom: 80,
          right: 0,
          minWidth: 180,
          maxWidth: 240,
          bgcolor: 'white',
          color: '#1976D2',
          px: 2.5,
          py: 2,
          borderRadius: 3,
          boxShadow: '0 8px 32px 0 rgba(25, 118, 210, 0.18)',
          fontWeight: 600,
          fontSize: '1.08rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          animation: 'fadeInUp 0.7s',
          zIndex: 20001,
          position: 'absolute',
        }}
      >
        {/* Arrow */}
        <Box sx={{
          position: 'absolute',
          bottom: -16,
          right: 32,
          width: 0,
          height: 0,
          borderLeft: '12px solid transparent',
          borderRight: '12px solid transparent',
          borderTop: '16px solid white',
          filter: 'drop-shadow(0 2px 4px #1976d233)'
        }} />
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1976D2', mr: 1 }}>Hi!</Typography>
          <span role="img" aria-label="wave" style={{ fontSize: 22, marginRight: 4 }}>👋</span>
          <IconButton size="small" sx={{ ml: 'auto', color: '#1976D2', p: 0.5 }} onClick={() => setShowHint(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography sx={{ mt: 1, fontWeight: 500, fontSize: '1.05rem', color: '#333', lineHeight: 1.5 }}>
          Use me for <span style={{ color: '#1976D2', fontWeight: 700 }}>help</span> or <span style={{ color: '#1976D2', fontWeight: 700 }}>chat</span>!
        </Typography>
      </Box>
    )}
    <Badge color="error" variant="dot" invisible={!hasNewMessage}>
      <Fab onClick={handleOpen}
        sx={{ background: 'linear-gradient(45deg, #1976D2 30%, #1565C0 90%)', '&:hover': { background: 'linear-gradient(45deg, #1E88E5 30%, #1976D2 90%)' }, boxShadow: showHint ? '0 0 0 6px #1976d233' : undefined, transition: 'box-shadow 0.3s' }}>
        <CustomBotFabIcon />
      </Fab>
    </Badge>
    {/* Keyframes for fadeInUp animation */}
    <style>{`
      @keyframes fadeInUp {
        0% { opacity: 0; transform: translateY(30px); }
        100% { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  </Box>

      {/* Chat Dialog */}
      <Dialog open={open} onClose={handleClose} TransitionComponent={Transition}
        PaperProps={{ sx: { position: 'fixed', bottom: 20, right: 20, m: 0, width: { xs: '90vw', sm: 400 }, height: { xs: '70vh', sm: 520 }, maxHeight: '80vh' } }}>
        <DialogTitle sx={{ background: 'linear-gradient(45deg, #1565C0 40%, #0D47A1 85%)', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: '#1976D2' }}><BotIcon /></Avatar>
            <Box><Typography variant="h6">InvoQuot Application</Typography><Typography variant="caption">{isTyping ? 'Typing…' : 'Online'}</Typography></Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{
          flex: 1, p: 1, backgroundColor: '#f5f5f5',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: '#eee', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb': { background: '#1976D2', borderRadius: '3px' }
        }} dividers>
          <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
            {messages.map((m) => (
              <Box key={m.id} sx={{ display: 'flex', justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: m.sender === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 1, maxWidth: '80%' }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: m.sender === 'user' ? '#1976D2' : '#1565C0' }}>
                    {m.sender === 'user' ? <PersonIcon /> : <BotIcon />}
                  </Avatar>
                  <Paper sx={{ p: 1.5, backgroundColor: m.sender === 'user' ? '#1976D2' : 'white', color: m.sender === 'user' ? 'white' : '#1976D2', borderRadius: 2, border: m.sender === 'bot' ? '1px solid #1976D2' : 'none' }}>
                    <Typography variant="body2">{m.text}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>{formatTime(m.timestamp)}</Typography>
                    {m.sender === 'bot' && !m.isQuestion && (<>{renderCategoryButtons()}{renderSuggestedQuestions()}</>)}
                  </Paper>
                </Box>
              </Box>
            ))}
            {isTyping && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ bgcolor: '#1565C0' }}><BotIcon /></Avatar><Typography>Typing...</Typography></Box>}
            <div ref={messagesEndRef} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: 'white' }}>
          <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
            <TextField fullWidth placeholder="Type your message…" value={inputMessage} onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendMessage(); } }} size="small" />
            <IconButton onClick={handleSendMessage} disabled={!inputMessage.trim()}
              sx={{ bgcolor: inputMessage.trim() ? '#1976D2' : '#ccc', color: 'white', '&:hover': { bgcolor: '#1565C0' } }}>
              <SendIcon />
            </IconButton>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FloatingChatbot;
